import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Typography, CardMedia, Tooltip, Icon } from '@mui/material'

import defaultImage from 'assets/images/default_avatar.png'

import AttendanceInfoDrawer from '../components/AttendanceInfoDrawer'
import AdxTableV2 from '../../../components/custom/AdxTableV2'
import AttendanceLabelRenderer from 'components/custom/AdxLabelRenderer'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import AdxCircularLoad from 'components/custom/AdxCircularLoad'
import AdxToolTip from 'components/custom/AdxToolTip'

const fallbackFromNumericStatus = (status) => {
  switch (status) {
    case 1:
      return 'ongoing'
    case 2:
      return 'present'
    case 3:
      return 'incomplete'
    case 4:
      return 'absent'
    case 5:
      return 'late'
    case 6:
      return 'late_and_incomplete'
    case 8:
      return 'on_leave'
    default:
      return 'unknown'
  }
}

const isToday = (date) => date && new Date(date).toDateString() === new Date().toDateString()

const getAttendanceStatus = (attendanceDay) => {
  const status =
    attendanceDay?.attendance?.derived_status ||
    attendanceDay?.derived_status ||
    fallbackFromNumericStatus(attendanceDay?.attendance?.status)

  if (status !== 'unknown') return status
  if (attendanceDay?.is_working_day === false) return 'non_working_day'
  if (isToday(attendanceDay?.date)) return 'not_clocked_in_yet'
  return status
}

const AttendanceCalendarView = ({
  attendanceData,
  setRowsPerPage,
  rowsPerPage,
  setPage,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  count,
  isLoading,
  search,
}) => {
  const navigate = useNavigate()
  const [employeeData, setEmployeeData] = useState([])

  // attendance info drawer related
  const [drawerState, setDrawerState] = useState({ right: false })
  const [attendanceDetails, setAttendanceDetails] = useState({
    profile_image: '',
    date: '',
    staffId: '',
    staffName: '',
    staffTitle: '',
    staffDept: '',
    status: '',
    type: '',
    clockInTime: '',
    clockInLocation: '',
    clockInNote: '',
    clockOutTime: '',
    clockOutLocation: '',
    clockOutNote: '',
    duration: '',
    overtime: '',
    isHoliday: '',
  })

  const handleOpenDrawer = (row) => {
    setAttendanceDetails({
      profile_image: row?.profile_image,
      date: row?.date,
      staffId: row?.staffId,
      staffName: row?.staffName,
      staffTitle: row?.staffTitle,
      staffDept: row?.staffDept,
      status: row?.status,
      type: row?.type,
      clockInTime: row?.clockInTime,
      clockInLocation: row?.clockInLocation,
      clockInNote: row?.clockInNote,
      clockOutTime: row?.clockOutTime,
      clockOutLocation: row?.clockOutLocation,
      clockOutNote: row?.clockOutNote,
      duration: row?.duration,
      overtime: row?.overtime,
      isHoliday: row?.isHoliday,
    })
    setDrawerState({ right: true })
  }

  const formattedToday = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
  })

  const generateDynamicColumns = (data) => {
    if (!data?.[0]?.attendances) {
      return []
    }

    return data[0].attendances.map((attendance, index) => {
      const formattedDate = new Date(attendance?.date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
      })

      return {
        Header: `${formattedDate === formattedToday ? 'Today' : formattedDate}`,
        accessor: `attendance_${index}`,
        Cell: (props) => {
          const attData = props.row.original
          const selectedBox = attData.attendances[index]
          const status = getAttendanceStatus(selectedBox)
          const statusReason =
            selectedBox.attendance?.status_reason || selectedBox.status_reason || null
          const statusLabel =
            selectedBox.attendance?.status_label || selectedBox.status_label || null
          return (
            <AdxToolTip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <AttendanceLabelRenderer
                    status={status}
                    withText
                    isHoliday={selectedBox.is_holiday}
                  />
                  {statusReason && (
                    <Typography sx={{ fontSize: '12px', maxWidth: '280px' }}>
                      {statusReason}
                    </Typography>
                  )}
                </Box>
              }
              placement="top"
              disableInteractive
              arrow
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '75px',
                  height: '89px',
                  bgcolor:
                    status === 'on_leave' || status === 'non_working_day' || !selectedBox.attendance
                      ? 'gray-background.main'
                      : '',
                  '&:hover': { bgcolor: 'gray-menu-hover.main' },
                }}
                onClick={() => {
                  handleOpenDrawer({
                    profile_image: attData.staff?.profile_image,
                    date: selectedBox.date,
                    staffId: attData.staff.id,
                    staffName: attData.staff?.name,
                    staffTitle: attData.staff?.position,
                    staffDept: attData.staff?.department,
                    status,
                    statusLabel,
                    statusReason,
                    type: selectedBox.attendance?.clockin?.mode_id,
                    clockInTime: selectedBox.attendance?.clockin?.clockin,
                    clockInLocation: selectedBox.attendance?.clockin?.street,
                    clockInNote: selectedBox.attendance?.clockin?.remarks,
                    clockOutTime: selectedBox.attendance?.clockout?.clockout,
                    clockOutLocation: selectedBox.attendance?.clockout?.street,
                    clockOutNote: selectedBox.attendance?.clockout?.remarks,
                    duration: selectedBox.attendance?.total_hours,
                    overtime: selectedBox.attendance?.overtime,
                    isHoliday: selectedBox.is_holiday,
                  })
                }}
              >
                <AttendanceLabelRenderer
                  status={status}
                  isHoliday={selectedBox.is_holiday}
                />
              </Box>
            </AdxToolTip>
          )
        },
      }
    })
  }

  const columns = useMemo(
    () => [
      {
        Header: 'Employee',
        accessor: 'name',
        Cell: (props) => {
          return (
            <Box
              sx={{
                bgcolor: 'white.main',
                width: '350px',
                height: '89px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 2,
                p: 2,
              }}
            >
              <CardMedia
                component="img"
                sx={{
                  height: 40,
                  width: 40,
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
                src={props?.row?.original?.staff?.profile_image || defaultImage}
              />
              <Box
                sx={{
                  wordWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  alignItems: 'start',
                  maxHeight: 80,
                }}
              >
                <Typography
                  sx={{
                    maxHeight: 40,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => navigate(`individual/${props?.row?.original?.staff?.id}`)}
                >
                  {props?.row?.original?.staff?.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'light-gray-text.main' }}>
                  {props?.row?.original?.staff?.position}, {props?.row?.original?.staff?.department}
                </Typography>
              </Box>
            </Box>
          )
        },
      },
      ...generateDynamicColumns(attendanceData),
    ],
    [attendanceData, formattedToday]
  )

  return (
    <>
      <Box sx={{ overflow: 'auto' }}>
        {generateDynamicColumns(attendanceData)?.length > 0 ? (
          <AdxTableV2
            column={columns}
            allColumnSorted={false}
            data={attendanceData}
            sxNav
            sxCell
            count={count}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            cellBorder={true}
            stickyHeader={true}
            stickyFirstColumn={true}
            height={{ xs: 'calc(100vh - 395px)', lg: 'calc(100vh - 325px)' }}
            pushScrollBarToEnd
            pagination
            highlightHeaderValue="Today"
            headerCentered={true}
          />
        ) : search.length > 0 ? (
          <AdxEmptyStateV2
            icon={<Icon>search_off</Icon>}
            title="No results found"
            subtitle={`It looks like we couldn't find any matches for your search. Try using different keywords.`}
          />
        ) : (
          <AdxCircularLoad />
        )}
      </Box>
      <AttendanceInfoDrawer
        drawerState={drawerState}
        setDrawerState={setDrawerState}
        attendanceDetails={attendanceDetails}
      />
    </>
  )
}

export default AttendanceCalendarView
